// Turning a pivot model into what a chart draws: one group per row (a bar, a
// point on the axis) and one series per column (a stack segment, a line),
// colored and folded the way the dataviz rules ask.
//
// Species keep their organization-wide color (useSpeciesColors). A chart can
// tell eight hues apart, so species past the organization's top eight fold
// into one "Other species" series instead of several identical grays; the
// non-species results keep their neutrals. Anything else (collection sites,
// years) is colored by its total in this chart, the top seven getting hues
// and the rest folding into "Other".
import { SERIES_COLORS, OTHER_COLOR, withOwnRanking } from "../../../../../utils/chartPalette";
import { cellValue, entryLabel } from "../PivotTable/pivotModel";

export const OTHER_SPECIES = "Other species";
const NONE = "none";

const isSpeciesAxis = (dims) => dims[0] === "species" || dims[0] === "genus";

/**
 * @returns {groups: [{name, key, r, total, values: {seriesKey: n}}], series: [{key,
 *   label, color, members?}], omitted: [{label, total}]} - series in draw
 *   order; `omitted` are the catch-all rows left off the chart (No collection
 *   site), for a note under it.
 */
export const chartSeries = (model, scale) => {
  const rows = model.rows.map((row, r) => ({ row, r })).filter(({ row }) => row.category[0] !== NONE);
  const omitted = model.rows
    .map((row, r) => ({ row, r }))
    .filter(({ row }) => row.category[0] === NONE)
    .map(({ row, r }) => ({ label: entryLabel(row), total: model.rowTotals[r] ?? 0 }));
  const columns = model.columns
    .map((col, c) => ({ col, c, total: model.columnTotals[c] ?? 0 }))
    .filter(({ col }) => col.category[0] !== NONE);
  omitted.push(
    ...model.columns
      .map((col, c) => ({ col, total: model.columnTotals[c] ?? 0 }))
      .filter(({ col }) => col.category[0] === NONE)
      .map(({ col, total }) => ({ label: entryLabel(col), total }))
  );

  // which series each column lands in
  let series;
  let seriesOf;
  if (isSpeciesAxis(model.colDims)) {
    const { colorOf, isOther } = withOwnRanking(
      scale,
      [...columns].sort((a, b) => b.total - a.total).map(({ col }) => entryLabel(col))
    );
    const own = columns.filter(({ col }) => col.category[0] !== "species" || !isOther(entryLabel(col)));
    const folded = columns.filter(({ col }) => col.category[0] === "species" && isOther(entryLabel(col)));
    // own colors in the server's order, Other species after the named ones
    // and before Unknown species / Bycatch
    const named = own.filter(({ col }) => col.category[0] === "species");
    const neutral = own.filter(({ col }) => col.category[0] !== "species");
    series = [
      ...named.map(({ col }) => ({ key: entryLabel(col), label: entryLabel(col), color: colorOf(entryLabel(col)) })),
      ...(folded.length
        ? [{ key: OTHER_SPECIES, label: OTHER_SPECIES, color: OTHER_COLOR, members: folded.map(({ col }) => entryLabel(col)) }]
        : []),
      ...neutral.map(({ col }) => ({ key: entryLabel(col), label: entryLabel(col), color: colorOf(entryLabel(col)) })),
    ];
    const foldedSet = new Set(folded.map(({ c }) => c));
    seriesOf = ({ col, c }) => (foldedSet.has(c) ? OTHER_SPECIES : entryLabel(col));
  } else {
    const ranked = [...columns].sort((a, b) => b.total - a.total);
    const keep = new Set(ranked.slice(0, SERIES_COLORS.length - 1).map(({ c }) => c));
    const rest = columns.filter(({ c }) => !keep.has(c));
    const other = `Other ${model.colDims[0] === "collectionSite" ? "collection sites" : "values"}`;
    // colors by rank, drawn in the axis's own order
    const colorByColumn = new Map(ranked.slice(0, SERIES_COLORS.length - 1).map(({ c }, i) => [c, SERIES_COLORS[i]]));
    series = [
      ...columns.filter(({ c }) => keep.has(c)).map(({ col, c }) => ({ key: entryLabel(col), label: entryLabel(col), color: colorByColumn.get(c) })),
      ...(rest.length > 1
        ? [{ key: other, label: other, color: OTHER_COLOR, members: rest.map(({ col }) => entryLabel(col)) }]
        : rest.map(({ col }) => ({ key: entryLabel(col), label: entryLabel(col), color: OTHER_COLOR }))),
    ];
    const restSet = new Set(rest.length > 1 ? rest.map(({ c }) => c) : []);
    seriesOf = ({ col, c }) => (restSet.has(c) ? other : entryLabel(col));
  }

  const groups = rows.map(({ row, r }) => {
    const values = {};
    columns.forEach((column) => {
      const v = cellValue(model, r, column.c);
      if (!v) return;
      const k = seriesOf(column);
      values[k] = (values[k] ?? 0) + v;
    });
    return { name: entryLabel(row), key: row.key, r, total: Object.values(values).reduce((a, b) => a + b, 0), values };
  });

  return { groups, series, omitted: omitted.filter((o) => o.total > 0) };
};

/** The chart's numbers as spreadsheet rows, every column unfolded. */
export const chartDataRows = (model, axisLabel) => [
  [axisLabel, ...model.columns.map(entryLabel)],
  ...model.rows.map((row, r) => [entryLabel(row), ...model.columns.map((_, c) => cellValue(model, r, c))]),
];
